// OverviewPanel — the Overview landing (account-area redesign Task 6, per the
// plan's 2026-10-02 amendment F2/F3/O1/O2). Replaces WelcomePanel.tsx (deleted
// by this task), whose "Recent Orders / Addresses count / Profile signpost"
// three-card layout the operator's amendment supersedes.
//
// The amendment's block order is BINDING (the operator's own ordering):
//   1. page header        — the shell renders it (AccountCenter's EmbedPageHeader)
//   2. attention strip    — only when non-empty (orders awaiting payment;
//                           renewals ending within the window)
//   3. Notifications      — gated read-notifications; unread count + the
//                           newest 3 unread rows INSIDE the block
//   4. Recent orders      — OrderCardList `compact`, at most 5
//   5. Profile            — company, contact name, email
//   6. Addresses          — gated manage-addresses; the DEFAULT shipping and
//                           billing addresses themselves (never a count
//                           signposting what the block can simply show)
//   7. Redemption activity — gated participate-referrals; the latest settled
//                           redemption + "View redemptions"
//
// SUPERSEDES (reconciled with the plan's pre-amendment Step 3/Step 4 text):
// the 4-up figures row is GONE (O1) — there is no open-orders / active-
// subscriptions / available-credit / next-renewal figure, and
// getMyReferralBalance() is NOT fetched here; the referral figure's
// replacement is GET /me/referral-redemptions?status=settled (same ACL area,
// same settle discipline), read for the latest SETTLED row. The plan's Step-4
// "figure tiles at 22px" browser proof is correspondingly replaced by the
// heading-size proof (Recent orders reads 14px, the body-large md size — see
// smoke-embed-account-shell.spec.ts's Overview case).
//
// FETCH DISCIPLINE (F3): every block's data arrives in ONE effect that waits
// for `selfViewSettled` — the panel mounts pre-settle (Overview is an
// ungated section, so AccountCenter's own area gate never holds it) but
// dispatches nothing until the first self-view attempt has resolved, and a
// DENIED area's request never fires at all. Each read degrades
// independently: a failed /me/account keeps the company from the self-view
// delivery and reads the empty marker for contact/email; a failed
// redemptions read renders "No settled redemptions yet." — never an error
// banner. The one deliberate exception is the ORDERS list, whose error and
// loading states are the shell's (passed down as props: the same list backs
// the Orders section, so Overview renders it, it never re-fetches it).
//
// Rule 111(b): the clock arrives through the injected `now` prop (defaulting
// to the wall clock only at the call boundary that does not test it); the
// renewal window is decided in UTC calendar days, the ProductsTab
// isReversibleCancellation idiom (cancel_at is a DATE; the server decides in
// UTC). A renewal row is ATTENTION only when the cancellation is still
// reversible (cancel_at > today) AND lands within
// RENEWAL_ATTENTION_WINDOW_DAYS — the strip is a work list, not a calendar.
//
// Inline styles only — no Tailwind (embed convention). Every <button> is
// explicitly type="button" (EmbedLink/EmbedButton do it at birth, rule 73).

import { useEffect, useState } from "react";
import {
  useEmbedTokens,
  EmbedButton,
  EmbedCard,
  EmbedHeading,
  EmbedLink,
  EmbedStatusBadge,
  EmbedText,
  EmbedEmptyState,
} from "../../embed-shared/primitives";
import { embedApiFetch } from "../../embed-shared/api-client";
import { formatCurrency } from "../../embed-shared/currency";
import { formatStatementMonth } from "../../embed-shared/format";
import type { CustomerAccount, OrderSummary, Subscription } from "../../embed-shared/types";
import { ACL_AREA_MANAGE_ADDRESSES, ACL_AREA_VIEW_PRODUCTS, ACL_AREA_MANAGE_SUBSCRIPTIONS, ACL_AREA_PARTICIPATE_REFERRALS, ACL_AREA_READ_NOTIFICATIONS } from "./membership-api";
import type { AclArea, SelfViewWire } from "./membership-api";
import { isAreaPermitted, type SectionId } from "./account-sections";
import { listMyNotifications } from "./notification-api";
import type { EmbedNotificationListResult } from "./notification-api";
import { listMyRedemptions } from "./referral-api";
import type { ReferralRedemptionWire } from "./referral-api";
import { NotificationListItem } from "./NotificationsPanel";
import { OrderCardList } from "./OrderCardList";

const EM_DASH = "—";

const ACCOUNT_PATH = "/api/v1/marketplace/me/account";
const SUBSCRIPTIONS_PATH = "/api/v1/marketplace/me/subscriptions";

/** Recent orders shows at most 5 cards (the amendment; the caller slices —
 *  OrderCardList itself has no count opinion). */
export const RECENT_ORDER_LIMIT = 5;

/** The Notifications block renders the newest 3 UNREAD rows (the amendment). */
const NOTIFICATION_ROW_LIMIT = 3;

/** How many days out a renewal becomes attention (the amendment's
 *  "renewal-ends-soon" row; a work list needs a horizon). */
export const RENEWAL_ATTENTION_WINDOW_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** UTC calendar-day start of a DATE string, or null when unparseable — the
 *  ProductsTab isReversibleCancellation idiom (cancel_at is a DATE; the
 *  server decides in UTC; local instants disagree either side of midnight). */
function dayStartUtcMs(value: string): number | null {
  const ms = new Date(`${value.slice(0, 10)}T00:00:00Z`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** A scheduled cancellation the server would still let the customer resume
 *  (cancel_at strictly after today, UTC). A same-day cancel_at leaves no
 *  resume window at all — attention must not point at a dead control. */
function isReversibleCancellation(cancelAt: string, now: () => Date): boolean {
  const scheduled = dayStartUtcMs(cancelAt);
  if (scheduled === null) return false;
  const d = now();
  return scheduled > Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** The renewal lands within the attention window (measured from today, UTC). */
function endsWithinWindow(cancelAt: string, now: () => Date): boolean {
  const scheduled = dayStartUtcMs(cancelAt);
  if (scheduled === null) return false;
  const d = now();
  const today = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return scheduled - today <= RENEWAL_ATTENTION_WINDOW_DAYS * DAY_MS;
}

function pluralize(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural;
}

export interface OverviewPanelProps {
  /** The settled (or null = failed/unloaded) self-view — gates the blocks. */
  selfView: SelfViewWire | null;
  /** True once the shell's first self-view attempt has resolved; every
   *  fetch waits for it (F3). Never true on first paint. */
  selfViewSettled: boolean;
  /** The shell's orders list (shared with the Orders section — never
   *  re-fetched here). */
  orders: OrderSummary[];
  loadingOrders: boolean;
  ordersError: string | null;
  /** The shell's address list (shared with the Profile section). */
  addresses: { id: number; address_type: string; is_default: boolean; line1: string; line2?: string; city: string; state: string; postal_code: string }[];
  loadingAddresses: boolean;
  /** Opens an order inside the shell (lands on Orders with the detail). */
  onOpenOrder: (orderId: string) => void;
  /** Cross-section navigation; the optional second argument is the REGISTRY
   *  tab id the destination section should open on (AccountCenter
   *  translates it to the destination panel's own vocabulary). */
  onNavigateSection: (id: SectionId, tab?: string) => void;
  /** The Orders empty state's next action (the catalog route). */
  onBrowseProducts?: () => void;
  /** Injected clock (rule 111(b)) — the renewal window reads this, never a
   *  bare new Date(). Defaults to the wall clock. */
  now?: () => Date;
}

export function OverviewPanel({
  selfView,
  selfViewSettled,
  orders,
  loadingOrders,
  ordersError,
  addresses,
  loadingAddresses,
  onOpenOrder,
  onNavigateSection,
  onBrowseProducts,
  now = () => new Date(),
}: OverviewPanelProps) {
  const t = useEmbedTokens();

  const [account, setAccount] = useState<CustomerAccount | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [settledRedemption, setSettledRedemption] = useState<ReferralRedemptionWire | null>(null);
  const [notifications, setNotifications] = useState<EmbedNotificationListResult | null>(null);

  // F3, render half: a gated block waits for the SETTLE as well as the
  // permission. `isAreaPermitted` is deliberately fail-open on an UNLOADED
  // map (a transient self-view failure must not blank the shell), so gating
  // the render on the permission alone would flash every gated block — and,
  // child effects first, its fetch — during the one round trip before the
  // self-view answer lands. Folding selfViewSettled into one predicate makes
  // the render gate and the effect gate the SAME gate (the four booleans
  // below are the effect's dep array, so the settle flip re-runs it and the
  // fetches fire exactly once the answer exists).
  const gatedOn = (area: AclArea) => selfViewSettled && isAreaPermitted(selfView, area);
  const notificationsPermitted = gatedOn(ACL_AREA_READ_NOTIFICATIONS);
  const subscriptionsPermitted = gatedOn(ACL_AREA_MANAGE_SUBSCRIPTIONS);
  const referralsPermitted = gatedOn(ACL_AREA_PARTICIPATE_REFERRALS);
  const addressesPermitted = gatedOn(ACL_AREA_MANAGE_ADDRESSES);
  // The renewal row's destination-visibility check — same predicate so a
  // pre-settle attention strip can never point at an unmounted section.
  // ⚠️ AMENDED 2026-10-10 (merge-review M2, TD-583): the destination is the
  // Products & subscriptions section, now gated by view-products; ORIGINAL
  // read manage-stores here because that was the section's gate.
  const productsPermitted = gatedOn(ACL_AREA_VIEW_PRODUCTS);

  // ONE effect per the amendment's F3 settle discipline: nothing fires until
  // the self-view has settled, and a denied area's request never fires. Each
  // read degrades independently (per-job catch — rule 110's lesson applied to
  // the client side: one failed read must not rename another's failure).
  useEffect(() => {
    if (!selfViewSettled) return;
    let cancelled = false;
    const jobs: Promise<void>[] = [];
    // /me/account is ungated (Profile shows it to every member); the company
    // also rides the self-view delivery as the fallback. Both setters
    // normalize the payload shape before it reaches state: a non-array
    // subscriptions payload (the shell suite's blanket detail mock resolves
    // EVERY path to one object) must degrade to an empty list, never crash
    // the whole landing in a .filter downstream — the panel's promise is
    // independent degradation, and that holds for a malformed shape too.
    jobs.push(
      embedApiFetch<CustomerAccount>(ACCOUNT_PATH)
        .then((res) => {
          if (!cancelled) {
            setAccount(res !== null && typeof res === "object" && !Array.isArray(res) ? res : null);
          }
        })
        .catch(() => {}),
    );
    if (subscriptionsPermitted) {
      jobs.push(
        embedApiFetch<Subscription[]>(SUBSCRIPTIONS_PATH)
          .then((res) => {
            if (!cancelled) setSubscriptions(Array.isArray(res) ? res : []);
          })
          .catch(() => {}),
      );
    }
    if (referralsPermitted) {
      jobs.push(
        // The amendment replaces the balance read: the latest SETTLED
        // redemption (the list is newest-first) is the activity figure.
        listMyRedemptions("settled")
          .then((rows) => {
            if (!cancelled) setSettledRedemption(rows[0] ?? null);
          })
          .catch(() => {}),
      );
    }
    if (notificationsPermitted) {
      jobs.push(
        listMyNotifications()
          .then((res) => {
            if (!cancelled) setNotifications(res);
          })
          .catch(() => {}),
      );
    }
    void Promise.all(jobs);
    return () => {
      cancelled = true;
    };
  }, [selfViewSettled, notificationsPermitted, subscriptionsPermitted, referralsPermitted]);

  // ---- Attention strip (amendment block 2): only when non-empty. ----
  interface AttentionRow {
    key: string;
    label: string;
    onActivate?: () => void;
  }
  const attentionRows: AttentionRow[] = [];
  const awaitingPayment = orders.filter((o) => o.status === "pending_payment");
  if (awaitingPayment.length > 0) {
    attentionRows.push({
      key: "payment",
      label: `${awaitingPayment.length} ${pluralize(awaitingPayment.length, "order", "orders")} awaiting payment`,
      onActivate: () => onNavigateSection("orders"),
    });
  }
  if (subscriptionsPermitted) {
    const endingSoon = subscriptions.filter(
      (s) =>
        s.status === "active" &&
        !!s.cancel_at &&
        isReversibleCancellation(s.cancel_at, now) &&
        endsWithinWindow(s.cancel_at, now),
    );
    if (endingSoon.length > 0) {
      attentionRows.push({
        key: "renewal",
        label: `${endingSoon.length} ${pluralize(endingSoon.length, "renewal", "renewals")} ${pluralize(endingSoon.length, "ends", "end")} soon`,
        // "Manage your renewal in Products & subscriptions" is only an
        // action when the destination section is itself visible.
        onActivate: productsPermitted ? () => onNavigateSection("products") : undefined,
      });
    }
  }

  const defaultShipping = addresses.find((a) => a.address_type === "shipping" && a.is_default);
  const defaultBilling = addresses.find((a) => a.address_type === "billing" && a.is_default);

  const company = account?.company_name ?? selfView?.account?.company_name ?? EM_DASH;
  const contactName = account?.display_name ?? EM_DASH;
  const email = account?.email ?? EM_DASH;

  const unreadRows = (notifications?.data ?? []).filter((n) => n.read_at === undefined);

  const addressLine = (a: { line1: string; line2?: string; city: string; state: string; postal_code: string }) =>
    [a.line1, a.line2, `${a.city}, ${a.state} ${a.postal_code}`].filter(Boolean).join(", ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.lg }}>
      {/* 2. Attention strip — absent entirely when nothing needs attention. */}
      {attentionRows.length > 0 && (
        <EmbedCard padding={t.spacing.md}>
          <div role="list" aria-label="Needs attention" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
            {attentionRows.map((row) => (
              <div key={row.key} role="listitem" style={{ display: "flex", alignItems: "baseline", gap: t.spacing.sm, minWidth: 0 }}>
                <span aria-hidden="true" style={{ color: t.colors.warning, flex: "0 0 auto" }}>•</span>
                {row.onActivate ? (
                  <EmbedLink onClick={row.onActivate}>{row.label}</EmbedLink>
                ) : (
                  <span style={{ fontSize: t.fontSize.sm, color: t.colors.text, minWidth: 0 }}>{row.label}</span>
                )}
              </div>
            ))}
          </div>
        </EmbedCard>
      )}

      {/* 3. Notifications — gated read-notifications; the newest 3 unread
          rows render INSIDE the block, the badge is the SERVER's count. */}
      {notificationsPermitted && (
        <section data-testid="overview-block-notifications" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: t.spacing.sm, flexWrap: "wrap" }}>
            <EmbedHeading level={3}>Notifications</EmbedHeading>
            {notifications && (
              <span
                aria-live="polite"
                style={{ fontSize: t.fontSize.xs, fontWeight: 600, color: notifications.unread > 0 ? t.colors.primary : t.colors.textSecondary }}
              >
                {notifications.unread === 0 ? "All read" : `${notifications.unread} unread`}
              </span>
            )}
          </div>
          {notifications && unreadRows.length > 0 && (
            <EmbedCard padding={`${t.spacing.sm}px ${t.spacing.md}px`}>
              <ul role="list" style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column" }}>
                {unreadRows.slice(0, NOTIFICATION_ROW_LIMIT).map((n) => (
                  <NotificationListItem key={n.id} n={n} />
                ))}
              </ul>
            </EmbedCard>
          )}
          <div>
            <EmbedLink onClick={() => onNavigateSection("notifications")}>View all notifications</EmbedLink>
          </div>
        </section>
      )}

      {/* 4. Recent orders — compact cards, at most 5. The list states are
          the shell's (shared list); only the empty state acts, routing to
          the catalog the way the Orders section's own empty state does. */}
      <section data-testid="overview-block-orders" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
        <EmbedHeading level={3}>Recent orders</EmbedHeading>
        {loadingOrders ? (
          <p role="status" style={{ margin: 0 }}>
            <EmbedText tone="secondary">Loading orders...</EmbedText>
          </p>
        ) : ordersError ? (
          <p role="alert" style={{ margin: 0 }}>
            <EmbedText tone="error">{ordersError}</EmbedText>
          </p>
        ) : orders.length === 0 ? (
          <EmbedEmptyState
            icon="orders"
            title="No orders yet."
            body="Orders you place will show up here."
            action={
              onBrowseProducts ? (
                <EmbedButton type="button" variant="secondary" onClick={onBrowseProducts}>
                  Browse products
                </EmbedButton>
              ) : undefined
            }
          />
        ) : (
          <OrderCardList orders={orders.slice(0, RECENT_ORDER_LIMIT)} onOpenOrder={onOpenOrder} compact />
        )}
        {orders.length > 0 && (
          <div>
            <EmbedLink onClick={() => onNavigateSection("orders")}>View all orders</EmbedLink>
          </div>
        )}
      </section>

      {/* 5. Profile — company, contact name, email. Ungated (the shell's
          Profile section is; these are the same facts). The account read
          degrades independently: company falls back to the self-view
          delivery, contact/email read the empty marker. */}
      <section data-testid="overview-block-profile" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
        <EmbedHeading level={3}>Profile</EmbedHeading>
        <EmbedCard padding={t.spacing.md}>
          <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Company</span>
              <span style={{ fontSize: t.fontSize.md, color: t.colors.text, overflowWrap: "anywhere" }}>{company}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Contact name</span>
              <span style={{ fontSize: t.fontSize.md, color: t.colors.text, overflowWrap: "anywhere" }}>{contactName}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Email</span>
              <span style={{ fontSize: t.fontSize.md, color: t.colors.text, overflowWrap: "anywhere" }}>{email}</span>
            </div>
          </div>
        </EmbedCard>
        <div>
          <EmbedLink onClick={() => onNavigateSection("profile")}>Edit profile</EmbedLink>
        </div>
      </section>

      {/* 6. Addresses — gated manage-addresses; the DEFAULT shipping and
          billing addresses themselves (never a count). */}
      {addressesPermitted && (
        <section data-testid="overview-block-addresses" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
          <EmbedHeading level={3}>Addresses</EmbedHeading>
          <EmbedCard padding={t.spacing.md}>
            <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
              {defaultShipping && (
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Default shipping</span>
                  <span style={{ fontSize: t.fontSize.md, color: t.colors.text, overflowWrap: "anywhere" }}>{addressLine(defaultShipping)}</span>
                </div>
              )}
              {defaultBilling && (
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Default billing</span>
                  <span style={{ fontSize: t.fontSize.md, color: t.colors.text, overflowWrap: "anywhere" }}>{addressLine(defaultBilling)}</span>
                </div>
              )}
              {!defaultShipping && !defaultBilling && (
                <EmbedText tone="secondary">{loadingAddresses ? "Loading addresses..." : "No saved addresses yet."}</EmbedText>
              )}
            </div>
          </EmbedCard>
          <div>
            <EmbedLink onClick={() => onNavigateSection("profile", "addresses")}>Manage addresses</EmbedLink>
          </div>
        </section>
      )}

      {/* 7. Redemption activity — gated participate-referrals; the latest
          SETTLED redemption (never the balance — O1 removed that read). */}
      {referralsPermitted && (
        <section data-testid="overview-block-redemptions" style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
          <EmbedHeading level={3}>Redemption activity</EmbedHeading>
          <EmbedCard padding={t.spacing.md}>
            {settledRedemption ? (
              <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm, flexWrap: "wrap", minWidth: 0 }}>
                <EmbedStatusBadge domain="redemption" value={settledRedemption.status} />
                <span style={{ fontSize: t.fontSize.md, color: t.colors.text, overflowWrap: "anywhere" }}>
                  {`${formatStatementMonth(settledRedemption.statement_month)} · ${formatCurrency(
                    Number(settledRedemption.verified_amount ?? settledRedemption.requested_amount),
                  )} applied`}
                </span>
              </div>
            ) : (
              <EmbedText tone="secondary">No settled redemptions yet.</EmbedText>
            )}
          </EmbedCard>
          <div>
            <EmbedLink onClick={() => onNavigateSection("refer", "redemptions")}>View redemptions</EmbedLink>
          </div>
        </section>
      )}
    </div>
  );
}
